import {
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Box,
  Button,
  TextField,
  FormControl,
  Switch,
  LinearProgress,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Container } from './PointControlForm.styles';

import { BsTrash } from 'react-icons/bs';

import moment from 'moment';
import { LocalizationProvider, TimePicker, ptBR } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import * as yup from 'yup';
import IPage from '../../../interfaces/IPage';
import { useToastrVanila } from '../../../hooks/util/useToastrVanila';
import { useValidateSchema } from '../../../hooks/providers/useValidateSchema';
import IFormError from '../../../interfaces/IFormError';
import ITimeRecord from '../../../interfaces/ITimeRecord';
import { useLocation } from 'react-router-dom';
import PageTitle from '../../../components/page-title/PageTitle';
import { MdOutlineAssignmentReturn } from 'react-icons/md';
import { usePointControlService } from '../../../hooks/services/usePointControlService';

interface TimeRecord {
  id: string;
  dateRecord: string;
  timeRecord: string;
  origin: number;
  action: string;
  status: string;
}

interface PointControlFormProps extends IPage {}

const PointControlForm = (props: PointControlFormProps) => {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<IFormError | null>(null);
  const [edit, setEdit] = useState<number | null>(null);
  const [rows, setRows] = useState<TimeRecord[] | []>([]);
  const [isAdding, setIsAdding] = useState(false);

  const request = usePointControlService();
  const toastr = useToastrVanila();
  const vallidate = useValidateSchema();
  const location = useLocation();

  moment.locale();

  const resource = location.pathname.split('/');
  const date = resource[2];
  const employeeId = Number(resource[3]);

  const find = async (date: string | undefined, employeeId: number, action: string[]) => {
    setLoading(true);
    await request
      .findTimeRecord(date, employeeId, action)
      .then(result => {
        if (result.length) {
          setRows(result);
          setLoading(false);
          setEdit(null);
          setIsAdding(false);
        }
        setLoading(false);
      })
      .catch(error => {
        toastr.error(error.message);
        setLoading(false);
      });
  };

  const create = async (row: ITimeRecord) => {
    setLoading(true);

    row.employeeId = employeeId;

    delete row.id;

    await request
      .createTimeRecord(row)
      .then(result => {
        find(date, employeeId, ['NONE', 'INSERT']);
      })
      .catch(error => {
        toastr.error(error.message);
        setLoading(false);
      });
  };

  const handleRemove = async (id: string, index: number) => {
    setLoading(true);

    const newRow = [...rows];
    newRow.splice(index, 1);
    setRows(newRow);

    if (id && !edit) {
      await request
        .removeTimeRecord(id)
        .then(() => {
          find(date, employeeId, ['NONE', 'INSERT']);
        })
        .catch(error => {
          toastr.error(error.message);
          setLoading(false);
        });
    }

    setIsAdding(false);
    setEdit(null);
  };

  const handleAdd = () => {
    setRows([
      ...rows,
      {
        id: '',
        dateRecord: String(date),
        timeRecord: '',
        origin: 2,
        action: 'NONE',
        status: 'PENDING',
      },
    ]);

    setIsAdding(true);
    setEdit(rows.length);
  };

  const handleInputChange = (value: string, index: number) => {
    setIsAdding(true);

    const novosDados = [...rows];

    const hour = value.split(' ');

    const newDate = `${moment(new Date()).format('yyyy-MM-DD')} ${hour[4]}`;

    novosDados[index].timeRecord = moment(newDate).format('yyyy-MM-DDTHH:mm:ss');

    setRows(novosDados);
  };

  const schema = yup.object({
    timeRecord: yup.string().required('Campo obrigatório'),
  });

  const handleSave = async () => {
    const last = rows[rows.length - 1];

    const result = await vallidate.validate(schema, last);

    if (!result.isValid) {
      setErrors(result.errors);
      return;
    } else {
      setErrors(null);
    }

    create(last);
    setEdit(null);
    setIsAdding(false);
  };

  const handleSaveWhenPressEnter = async (e: any) => {
    if (e.key === 'Enter') {
      handleSave();
    }
  };

  const handleShowAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      find(date, employeeId, ['NONE', 'INSERT', 'DELETE', 'IGNORE']);
    } else {
      find(date, employeeId, ['NONE', 'INSERT']);
    }
  };

  useEffect(() => {
    find(date, employeeId, ['NONE', 'INSERT']);
    handleAdd();
  }, []);

  return (
    <Container>
      <PageTitle title={`EDIÇÃO DE PONTO`}>
        <Tooltip title="Voltar para listagem" placement="left-start">
          <IconButton onClick={() => window.history.back()} color="inherit">
            <MdOutlineAssignmentReturn />
          </IconButton>
        </Tooltip>
      </PageTitle>
      {loading && <LinearProgress />}

      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', padding: '80px 0px 10px 0px', marginBottom: '45px', alignContent: 'flex-start' }}>
          <Button variant="contained" onClick={handleAdd} disabled={isAdding}>
            Adicionar
          </Button>
        </Box>

        <Box sx={{ display: 'flex', padding: '50px 0px 10px 0px', alignContent: 'flex-start' }}>
          <div style={{ display: 'flex', flexDirection: 'column', marginLeft: '50px', marginRight: '50px' }}>
            <label>Todas as marcações</label>
            <Switch onChange={handleShowAll}></Switch>
          </div>
        </Box>
      </div>

      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Dias</TableCell>
            <TableCell>Horário</TableCell>
            <TableCell>Ações</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => (
            <TableRow key={index}>
              {edit === index ? (
                <>
                  <TableCell>{moment(row.dateRecord).format('DD/MM')}</TableCell>
                  <TableCell style={{ maxWidth: '80px' }}>
                    <LocalizationProvider
                      dateAdapter={AdapterMoment}
                      localeText={ptBR.components.MuiLocalizationProvider.defaultProps.localeText}
                    >
                      <FormControl
                        style={{
                          width: '150px',
                        }}
                        onKeyUp={handleSaveWhenPressEnter}
                      >
                        <TimePicker
                          value={row.timeRecord}
                          label="Horário"
                          onChange={newValue => handleInputChange(String(newValue), index)}
                          renderInput={params => (
                            <TextField
                              {...params}
                              size="small"
                              placeholder="Selecione o horário"
                              error={Boolean(errors?.timeRecord)}
                              helperText={errors?.timeRecord}
                            />
                          )}
                        />
                      </FormControl>
                    </LocalizationProvider>
                  </TableCell>
                </>
              ) : (
                <>
                  <TableCell>{moment(row.dateRecord).format('DD/MM')}</TableCell>
                  <TableCell>{moment(row.timeRecord).format('LT')}</TableCell>
                </>
              )}
              <TableCell>
                <Tooltip title="Deletar" placement="top">
                  <IconButton
                    disabled={row.action === 'DELETE'}
                    onClick={async () => {
                      await handleRemove(row.id, index);
                      setLoading(false);
                    }}
                  >
                    <BsTrash style={{ fontSize: '15px' }} />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Box sx={{ padding: '50px 0px 10px 0px' }}>
        <Button variant="contained" onClick={handleSave} disabled={!isAdding}>
          Salvar
        </Button>
      </Box>
    </Container>
  );
};

export default PointControlForm;
